import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import {
  ListCellDefDirective,
  ListModuleColumnDef,
  ListModuleTableComponent,
} from 'src/app/shared/components/list-module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { HasPermissionPipe } from 'src/app/core/pipes/has-permission.pipe';
import { TenantPhonePipe } from 'src/app/shared/pipes/tenant-phone.pipe';
import { SUPER_ADMIN_PERMISSION_SLUGS } from 'src/app/core/constants/super-admin-permission-slugs';
import type { StaffUserRow } from 'src/app/core/services/staff-user-api.service';
import { adminUserDetailPath } from '../admin-user-route.util';
import { ActionMenuPanelDirective } from 'src/app/shared/directives/action-menu-panel.directive';
import { isSuperAdminRoleSlug } from 'src/app/core/constants/super-admin-role';

@Component({
  selector: 'app-manage-admin-users-table',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    IconModule,
    HasPermissionPipe,
    TenantPhonePipe,
    ListModuleTableComponent,
    ListCellDefDirective,
    ActionMenuPanelDirective,
  ],
  templateUrl: './manage-admin-users-table.component.html',
})
export class ManageAdminUsersTableComponent {
  readonly P = SUPER_ADMIN_PERMISSION_SLUGS;

  @Input() rows: StaffUserRow[] = [];
  @Input() view: 'active' | 'deleted' = 'active';
  @Input() loading = false;
  @Input() emptyMessage = '';
  @Input() openActionMenuKey: string | null = null;

  @Output() toggleMenu = new EventEmitter<{ key: string; event: MouseEvent }>();
  @Output() closeMenu = new EventEmitter<void>();
  @Output() edit = new EventEmitter<StaffUserRow>();
  @Output() delete = new EventEmitter<StaffUserRow>();
  @Output() restore = new EventEmitter<StaffUserRow>();
  @Output() permanentDelete = new EventEmitter<StaffUserRow>();
  @Output() toggleStatus = new EventEmitter<StaffUserRow>();
  @Output() resendLink = new EventEmitter<StaffUserRow>();
  @Output() viewDetail = new EventEmitter<StaffUserRow>();

  get columns(): ListModuleColumnDef[] {
    return [
      { id: 'id', header: 'ID', cellClass: 'whitespace-nowrap font-mono text-xs' },
      { id: 'user', header: 'User' },
      { id: 'phone', header: 'Phone', cellClass: 'whitespace-nowrap font-mono text-sm' },
      { id: 'email', header: 'Email', cellClass: 'max-w-[200px] truncate text-sm' },
      {
        id: 'date',
        header: this.view === 'deleted' ? 'Deleted' : 'Created',
        cellClass: 'whitespace-nowrap text-sm',
      },
      { id: 'status', header: 'Status', align: 'center', cellClass: 'whitespace-nowrap text-xs font-bold' },
      { id: 'actions', header: 'Action', align: 'right', cellClass: 'text-end' },
    ];
  }

  actionMenuRowKey(index: number): string {
    return this.view === 'deleted' ? `deleted-${index}` : `active-${index}`;
  }

  userIdLabel(row: StaffUserRow): string {
    return row.unique_id ?? (row.id == null ? '—' : `ID-${row.id}`);
  }

  userDetailRoute(row: StaffUserRow): string[] | null {
    return adminUserDetailPath(row);
  }

  profileImageUrl(row: StaffUserRow): string | null {
    const pp = row.profile_picture;
    if (!pp) return null;
    if (typeof pp === 'string') return pp;
    if (typeof pp === 'object' && pp !== null) {
      const o = pp as Record<string, unknown>;
      if (typeof o['preview_url'] === 'string') return o['preview_url'];
    }
    return null;
  }

  initials(name: unknown): string {
    const n = typeof name === 'string' ? name.trim() : '';
    return n ? n.charAt(0).toUpperCase() : 'U';
  }

  roleLabel(row: StaffUserRow): string | null {
    const role = row?.user_roles?.[0]?.role;
    return role?.role_name ?? role?.slug ?? null;
  }

  isProtectedSuperAdminUser(row: StaffUserRow): boolean {
    return (row.user_roles ?? []).some((ur) =>
      isSuperAdminRoleSlug(ur.role?.slug),
    );
  }

  statusBadgeClass(status: unknown): Record<string, boolean> {
    const s = typeof status === 'string' ? status : '';
    return {
      'text-success bg-success-light': s === 'Active',
      'text-secondary bg-secondary-light': s === 'InActive',
      'text-danger bg-danger-light': s === 'Blocked',
      'text-warning bg-warning-light': s === 'Suspended' || s === 'Locked',
    };
  }
}
